// Copyright (c) 2019 by Michael Marucheck
//
// Use of this source code is governed by an MIT-style license that
// can be found in the LICENSE file distributed with this file.

import uuidv4 from "uuid/v4";
import React, { useState, useEffect } from "react";
import { RouteComponentProps, withRouter } from "react-router-dom";

import { Volunteer, SchoolDocument } from "../store/schema";
import { getApi, volCache } from "../store/api";
import { encouragement, classYearString, classYearNumber } from "../util";
import { SchoolChooser } from "../components/SchoolChooser";
import { update, useWidthGate, observe, Lifecycle } from "../UiUtil";
import { breadcrumbs, URLS } from "../urls";
import { NavBar, navLink } from "../components/NavBar";
import { acceptedAllRequiredMarkdowns } from "../components/PendingMarkdowns";
import {
  validate,
  CLASS_YEAR_VALIDATORS,
  PHONE_NUMBER_VALIDATORS,
  UID_VALIDATORS,
  Concern,
  ValidatedTextInputDetail,
  FIRST_LAST_NAME_VALIDATORS,
  OPTIONAL_NAME_VALIDATORS,
  ValidatedDetail,
  ValidatedPhoneInputDetail,
} from "../components/ValidatedUI";
import { SaveButton } from "../components/SaveButton";
import { PageContainer } from "../components/PageContainer";
import { CancelButton } from "../components/CancelButton";
import { Breadcrumbs } from "../components/BreadCrumb";

const WIDTH_GATE = {
  wideNav: 501,
};

const Page: React.FC<RouteComponentProps<{}>> = ({ history }) => {
  debug("======= ProfilePage =======");
  const [state, setState] = useState(volCache().get());
  const widthBucket = useWidthGate(WIDTH_GATE);
  const [lifecycle, setLifecycle] = useState(Lifecycle.VIEWED);
  const edited = () => setLifecycle(Lifecycle.EDITED);

  const { volunteerLoaded, user, volunteer: volunteerDoc } = state;
  const email = (user && user.email) || "";
  const uid = (user && user.uid) || "";
  const volunteer =
    (volunteerDoc && volunteerDoc.data) || emptyVolunteer(email);

  const [schools, setSchools] = useState<SchoolDocument[]>([]);

  const [name, setName] = observe(edited, useState(volunteer.name));
  const [preferredName, setPreferredName] = observe(
    edited,
    useState(volunteer.preferredName)
  );
  const [phone, setPhone] = observe(edited, useState(volunteer.phone));
  const [classYear, setClassYear] = observe(
    edited,
    useState(classYearString(volunteer.classYear))
  );
  const [schoolUid, setSchoolUid] = observe(
    edited,
    useState(volunteer.schoolUid)
  );

  const api = getApi();

  const school = (schools || []).find(x => x.data.uid === schoolUid) || null;

  function updateSchoolUid(uid: string) {
    update("schoolUid", schoolUid, setSchoolUid, uid);
  }

  useEffect(() => {
    getApi()
      .loadSchools()
      .then(setSchools);
  }, []);

  useEffect(() =>
    volCache().subscribe(newState => {
      if (update("setState", state, setState, newState)) {
        if (lifecycle !== Lifecycle.EDITED) {
          const vDoc = newState.volunteer;
          initializeForm(vDoc == null ? emptyVolunteer(email) : vDoc.data);
          debug(`state changed, updated form to match new state`);
        } else {
          debug(`state changed but form is edited, ignoring update`, {
            cache: volunteer,
            form: parseForm(),
          });
        }
      } else {
        debug(`volCache notified of update but nothing changed`);
      }
    })
  );

  function emptyVolunteer(email: string): Volunteer {
    return {
      uid,
      email,
      name: "",
      phone: "",
      preferredName: "",
      schoolUid: "",
      classYear: classYearNumber(""),
      acceptedMarkdowns: [],
    };
  }

  function initializeForm(volunteer: Volunteer) {
    update("name", name, setName, volunteer.name);
    update(
      "setPreferredName",
      preferredName,
      setPreferredName,
      volunteer.preferredName || ""
    );
    update(
      "classYear",
      classYear,
      setClassYear,
      classYearString(volunteer.classYear)
    );
    update("phone", phone, setPhone, volunteer.phone);

    debug(`Setting schoolUid='${volunteer.schoolUid}'`);
    update("schoolUid", schoolUid, setSchoolUid, volunteer.schoolUid);
    setLifecycle(Lifecycle.VIEWED);
  }

  function parseForm(): Volunteer {
    return {
      uid,
      email,
      name,
      phone,
      preferredName,
      classYear: classYearNumber(classYear),
      schoolUid,
      acceptedMarkdowns: volunteer.acceptedMarkdowns,
    };
  }

  const concerns: { [key: string]: Concern | null } = {
    name: validate(FIRST_LAST_NAME_VALIDATORS, name),
    preferredName: validate(OPTIONAL_NAME_VALIDATORS, preferredName || ""),
    classYear: validate(CLASS_YEAR_VALIDATORS, classYear),
    phone: validate(PHONE_NUMBER_VALIDATORS, phone),
    schoolUid: validate(UID_VALIDATORS, schoolUid, true),
  };

  async function save() {
    const v = parseForm();
    debug("Saving volunteer", v);
    await api.volunteer.saveVolunteer(v);

    if (!acceptedAllRequiredMarkdowns(volunteer, school)) {
      debug("Saved volunteer, navigating");
      history.push(URLS.vHome());
    }
    debug("save() exiting");
  }

  return (
    <PageContainer
      title="Volunteer Profile"
      crumbs={breadcrumbs().vHome}
      administrators={state.administrators}
      school={state.school}
      checks={{ user, loaded: volunteerLoaded }}
    >
      <div className="details-form">
        <div className="details">
          <div className="detail">
            <label htmlFor="email">Login Email:</label>
            <input id="email" type="text" value={email} disabled={true} />
          </div>
          <ValidatedTextInputDetail
            htmlId="name"
            label="Name:"
            value={name}
            placeholder="First Last (name in school records)"
            onChange={setName}
            concern={concerns.name}
          />
          <ValidatedTextInputDetail
            htmlId="preferred"
            label="Preferred Name:"
            value={preferredName || ""}
            placeholder="(optional) if different from school records"
            onChange={setPreferredName}
            concern={concerns.preferredName}
          />
          <ValidatedPhoneInputDetail
            htmlId="phone"
            label="Phone:"
            value={phone}
            onChange={setPhone}
            concern={concerns.phone}
          />
          <ValidatedDetail
            htmlFor="school"
            label="School:"
            concern={concerns.schoolUid}
          >
            <SchoolChooser
              id="school"
              schoolUid={schoolUid}
              schools={schools}
              onChangeSchool={updateSchoolUid}
            />
          </ValidatedDetail>
          <ValidatedTextInputDetail
            htmlId="class-year"
            label="Class Year:"
            inputMode="numeric"
            value={classYear}
            placeholder={`e.g. ${defaultClassYear()}`}
            onChange={setClassYear}
            concern={concerns.classYear}
          />
        </div>
        <div className="section">
          Please make sure this information is correct. If you choose the wrong
          school or wrong class year, your coordinator will not see your
          volunteer activities.
        </div>
        <div className="done">
          <SaveButton
            lifecycle={lifecycle}
            setLifecycle={setLifecycle}
            concerns={concerns}
            onSave={save}
          />
          <CancelButton
            lifecycle={lifecycle}
            setLifecycle={setLifecycle}
            onCancel={() => initializeForm(volunteer || emptyVolunteer(email))}
          />
        </div>
        {lifecycle === Lifecycle.SAVED && (
          <>
            <div className="nav">
              {encouragement()} Here are some ideas for where to go next:
            </div>
            <NavBar
              wide={widthBucket >= WIDTH_GATE.wideNav}
              links={[
                navLink(URLS.index(), "home"),
                navLink(URLS.vActivitiesDetail(uuidv4()), "Enter New Activity"),
                navLink(URLS.vActivitiesAll(), "View All Activities"),
              ]}
            />
          </>
        )}
      </div>
      {lifecycle === Lifecycle.VIEWED && (
        <>
          <div className="section"></div>
          <Breadcrumbs crumbs={breadcrumbs().vHome} />
        </>
      )}
    </PageContainer>
  );
};
export const ProfilePage = withRouter(Page);

function defaultClassYear() {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth() + 1;
  return month > 6 ? year + 1 : year;
}

function debug(message: string, state?: any) {
  if (true) {
    console.log(`ProfilePage (debug): ${message}`);
    if (state != null) {
      console.log(state);
    }
  }
}
